import { CommonModule } from '@angular/common';
import { Component, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';

import { AuthService } from '../auth.service';
import { DevAuthApiService } from './dev-auth-api.service';
import { TranslatePipe } from '../../i18n/translate.pipe';
import { TranslationService } from '../../i18n/translation.service';

/**
 * TEST-ONLY login screen ("admin"/"admin", full privilege) standing in for the real
 * external Authenticator Service's own login page. Calls
 * FiveWORX.Communication.API.Controllers.DevAuthController, which only exists while the
 * API is running as Development -- see that controller's own remarks. Replace this whole
 * page with a redirect to the real Authenticator Service's login flow when that
 * integration happens; AuthCallbackComponent already exists for the return leg.
 */
@Component({
  selector: 'app-login-page',
  standalone: true,
  imports: [CommonModule, FormsModule, ButtonModule, InputTextModule, TranslatePipe],
  templateUrl: './login.page.html',
})
export class LoginPage {
  private readonly devAuthApi = inject(DevAuthApiService);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly translationService = inject(TranslationService);

  readonly username = signal('');
  readonly password = signal('');
  readonly signingIn = signal(false);
  readonly error = signal<string | null>(null);

  signIn(): void {
    if (this.signingIn()) {
      return;
    }

    this.signingIn.set(true);
    this.error.set(null);

    this.devAuthApi.login(this.username().trim(), this.password()).subscribe({
      next: (result) => {
        this.signingIn.set(false);
        this.authService.setToken(result.token);
        void this.router.navigateByUrl('/');
      },
      error: () => {
        this.signingIn.set(false);
        this.error.set(this.translationService.translate('auth.invalidCredentials'));
      },
    });
  }
}
